import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useMutation, useQuery } from '@tanstack/react-query';

import { parseApiError, useAuth, usePermission } from '@saas-kit/lib';
import { ErrorBanner, PageHeader, toast } from '@saas-kit/ui';

import { TenantRequiredState } from '../../components/TenantRequiredState';
import { fetchBranding } from './apiBranding';
import { listCounters, listCustomerTypes, listServices } from './apiCatalog';
import { issueToken } from './apiWorkstation';
import { IssuedTokenCard } from './IssuedTokenCard';
import { LastIssuedTokenCard } from './LastIssuedTokenCard';
import { LiveQueuePanel } from './LiveQueuePanel';
import { QmsIssueForm } from './QmsIssueForm';
import type { QmsToken } from './types';

/** Standalone "Issue token" page — the reception/supervisor hub. Beyond
 *  issuing tokens it surfaces live queue control so staff are not left at a
 *  dead end after the receipt prints. */
export function QmsIssuePage(): JSX.Element {
  const { t, i18n } = useTranslation('qms');
  const selectedTenant = useAuth((s) => s.selectedTenant);
  const canIssue = usePermission('qms.tokens.issue');
  const canView = usePermission('qms.tokens.view');
  const canForceCall = usePermission('qms.tokens.force_call');
  const tenantId = selectedTenant?.tenant_id;

  const [issued, setIssued] = useState<QmsToken | null>(null);

  const servicesQuery = useQuery({
    queryKey: ['qms-services', tenantId],
    queryFn: listServices,
    enabled: Boolean(tenantId && canIssue),
  });
  const customerTypesQuery = useQuery({
    queryKey: ['qms-customer-types', tenantId],
    queryFn: listCustomerTypes,
    enabled: Boolean(tenantId && canIssue),
  });
  const brandingQuery = useQuery({
    queryKey: ['qms-branding', tenantId],
    queryFn: fetchBranding,
    enabled: Boolean(tenantId && canIssue),
    staleTime: 60_000,
  });
  const countersQuery = useQuery({
    queryKey: ['qms-counters', tenantId],
    queryFn: listCounters,
    enabled: Boolean(tenantId && canIssue && (canForceCall || canView)),
    refetchInterval: 15_000,
  });

  const services = (servicesQuery.data ?? []).filter((s) => s.is_active);
  const customerTypes = (customerTypesQuery.data ?? []).filter(
    (c) => c.is_active,
  );
  const counters = countersQuery.data ?? [];

  const issue = useMutation({
    mutationFn: issueToken,
    onSuccess: (token) => {
      setIssued(token);
      toast.success(t('issue.issued', { code: token.display_code }));
    },
    onError: (error) => toast.error(parseApiError(error).message),
  });

  if (!selectedTenant) {
    return (
      <TenantRequiredState
        title={t('tenant_required.title')}
        body={t('tenant_required.body')}
        cta={t('tenant_required.cta')}
      />
    );
  }
  if (!canIssue) {
    return <ErrorBanner>{t('issue.denied')}</ErrorBanner>;
  }

  const issuedServiceName =
    services.find((s) => s.id === issued?.service_id)?.name ?? '';

  return (
    <div className="space-y-6">
      <PageHeader title={t('issue.title')} description={t('issue.subtitle')} />

      <div className={canView ? 'grid gap-6 lg:grid-cols-3' : 'space-y-6'}>
        <div className="space-y-6 lg:col-span-2">
          {issued ? (
            <>
              <IssuedTokenCard
                token={issued}
                serviceName={issuedServiceName}
                branding={brandingQuery.data ?? null}
                locale={i18n.language}
                onIssueAnother={() => setIssued(null)}
              />
              {canForceCall || canView ? (
                <LastIssuedTokenCard
                  token={issued}
                  serviceName={issuedServiceName}
                  counters={counters}
                  canForceCall={canForceCall}
                  canView={canView}
                />
              ) : null}
            </>
          ) : (
            <QmsIssueForm
              services={services}
              customerTypes={customerTypes}
              pending={issue.isPending}
              onSubmit={(input) => issue.mutate(input)}
            />
          )}
        </div>

        {canView ? (
          <div className="lg:col-span-1">
            <LiveQueuePanel counters={counters} canForceCall={canForceCall} />
          </div>
        ) : null}
      </div>
    </div>
  );
}
